// src/pages/ChatListPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Badge, Box, CircularProgress, Divider,
  InputBase, List, ListItem, ListItemAvatar, ListItemText,
  Typography, Fab, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  collection, onSnapshot, orderBy, query,
  where, doc, onSnapshot as onSnap, getDoc, getDocs,
  arrayUnion, updateDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import CreateChatDialog from '../components/CreateChatDialog';

const PURPLE = '#7C3AED';

const normalizeRole = (r = '') => {
  const v = r.toLowerCase();
  if (v.includes('admin')) return 'admin';
  if (v.includes('prof') || v.includes('teacher')) return 'teacher';
  if (v.includes('parent')) return 'parent';
  return 'student';
};

const TYPE_ICONS = { course: '🎓', direct: '👤', group: '👥' };
const TYPE_COLORS = { course: '#1E88E5', direct: '#43A047', group: PURPLE };

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

// ── Tuile d'un chat ───────────────────────────────────────────────────────
const ChatTile = ({ chat, myUid, onClick }) => {
  const [unread, setUnread] = useState(0);
  const type = chat.type || 'group';
  const color = TYPE_COLORS[type] || PURPLE;
  const icon = TYPE_ICONS[type] || '💬';

  useEffect(() => {
    const memberRef = doc(db, 'chats', chat.id, 'members', myUid);
    const unsub = onSnap(memberRef, async (snap) => {
      const lastReadAt = snap.data()?.lastReadAt;
      try {
        const { getDocs, collection: col, query: q, where: w } = await import('firebase/firestore');
        let query = q(col(db, 'chats', chat.id, 'messages'), w('senderId', '!=', myUid));
        if (lastReadAt) {
          query = q(
            col(db, 'chats', chat.id, 'messages'),
            w('senderId', '!=', myUid),
            w('createdAt', '>', lastReadAt),
          );
        }
        const s = await getDocs(query);
        setUnread(s.size);
      } catch { setUnread(0); }
    });
    return () => unsub();
  }, [chat.id, myUid]);

  const preview = chat.lastMessageSenderName && chat.lastMessageText
    ? `${chat.lastMessageSenderName} : ${chat.lastMessageText}`
    : (chat.lastMessageText || '');

  return (
    <ListItem button onClick={onClick} sx={{
      py: 1.5, px: 2,
      '&:hover': { bgcolor: '#F9F5FF' },
      bgcolor: unread > 0 ? '#fafafa' : 'transparent',
    }}>
      <ListItemAvatar>
        <Badge badgeContent={unread > 99 ? '99+' : unread} color="secondary"
          sx={{ '& .MuiBadge-badge': { bgcolor: PURPLE, color: '#fff', fontSize: 10 } }}>
          <Avatar sx={{ bgcolor: color + '20', color, fontSize: 18, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ flex: 1 }}>
              {chat.name || 'Chat'}
            </Typography>
            <Typography variant="caption" sx={{ color: unread > 0 ? PURPLE : 'text.secondary', ml: 1, flexShrink: 0 }}>
              {fmtTime(chat.lastMessageAt)}
            </Typography>
          </Box>
        }
        secondary={
          <Typography variant="caption" noWrap
            sx={{ color: unread > 0 ? 'text.primary' : 'text.secondary',
              fontWeight: unread > 0 ? 500 : 400 }}>
            {preview || 'Aucun message'}
          </Typography>
        }
      />
    </ListItem>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════
export default function ChatListPage() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const me = auth.currentUser;

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  // Vérifier droits (admin ou prof)
  useEffect(() => {
    if (!me) return;
    const check = async () => {
      try {
        const adminSnap = await import('firebase/firestore').then(({ getDoc, doc: d }) =>
          getDoc(d(db, 'admins', me.uid))
        );
        if (adminSnap.exists()) { setCanCreate(true); return; }
        const userSnap = await import('firebase/firestore').then(({ getDoc, doc: d }) =>
          getDoc(d(db, 'users', me.uid))
        );
        setCanCreate(normalizeRole(userSnap.data()?.role || '') === 'teacher');
      } catch { setCanCreate(false); }
    };
    check();
  }, [me]);

  // ── Chargement combiné : participants[] + chats de cours ─────────────────
  useEffect(() => {
    if (!me) { setLoading(false); return; }
    setLoading(true);

    // Source 1 : chats où l'admin est explicitement participant
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', me.uid),
      orderBy('lastMessageAt', 'desc'),
    );

    const unsub = onSnapshot(q, async (snap) => {
      const directChats = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const chatIds = new Set(directChats.map(c => c.id));

      // Source 2 : tous les chats de type 'course' (admin voit tout)
      try {
        const courseChatsSnap = await getDocs(
          query(collection(db, 'chats'), where('type', '==', 'course'))
        );

        const extraChats = [];
        for (const docSnap of courseChatsSnap.docs) {
          if (!chatIds.has(docSnap.id)) {
            const chatData = { id: docSnap.id, ...docSnap.data() };
            extraChats.push(chatData);
            chatIds.add(docSnap.id);

            // Auto-ajouter l'admin dans participants pour les prochaines fois
            if (!(docSnap.data().participants || []).includes(me.uid)) {
              updateDoc(doc(db, 'chats', docSnap.id), {
                participants: arrayUnion(me.uid),
              }).catch(() => {});
            }
          }
        }

        // Fusionner et trier par lastMessageAt
        const all = [...directChats, ...extraChats].sort((a, b) => {
          const aTs = a.lastMessageAt?.seconds || 0;
          const bTs = b.lastMessageAt?.seconds || 0;
          return bTs - aTs;
        });

        setChats(all);
      } catch {
        // Si erreur source 2, afficher au moins les chats directs
        setChats(directChats);
      }

      setLoading(false);
    }, () => setLoading(false));

    return () => unsub();
  }, [me]);

  const filtered = chats.filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!me) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography>Veuillez vous connecter.</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F5F5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: PURPLE, px: 2, py: 2 }}>
        <Typography variant="h6" color="#fff" fontWeight="bold" mb={1.5}>
          Messages
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.15)',
          borderRadius: 3, px: 1.5, py: 0.5 }}>
          <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 18 }} />
          <InputBase placeholder="Rechercher…" value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ color: '#fff', flex: 1, fontSize: 14 }} />
        </Box>
      </Box>

      {/* Liste */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
            <Typography variant="h4">💬</Typography>
            <Typography variant="body2" mt={1}>
              {search ? 'Aucun résultat' : 'Aucune conversation'}
            </Typography>
            {canCreate && !search && (
              <Typography variant="caption" mt={0.5} display="block">
                Créez votre premier chat avec le bouton +
              </Typography>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {filtered.map((chat, i) => (
              <React.Fragment key={chat.id}>
                <ChatTile chat={chat} myUid={me.uid}
                  onClick={() => navigate(`/chats/${chat.id}`)} />
                {i < filtered.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {canCreate && (
        <Tooltip title="Nouveau chat">
          <Fab onClick={() => setOpenCreate(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24, bgcolor: PURPLE, color: '#fff',
              '&:hover': { bgcolor: '#6D28D9' } }}>
            <AddIcon />
          </Fab>
        </Tooltip>
      )}

      <CreateChatDialog open={openCreate} onClose={() => setOpenCreate(false)} myUid={me.uid} />
    </Box>
  );
}